import { jsx as _jsx } from "react/jsx-runtime";
import { memoForwardRef } from "@shared/client/util/memo";
import "overlayscrollbars/overlayscrollbars.css";
import { OverlayScrollbarsComponent, } from "overlayscrollbars-react";
import React from "react";
import "./scroll-view.scss";
/**
 * `ScrollView` is our app-wide scroll container. It paints overlay-style
 * scrollbars on top of content (so content doesn't reflow when the bar
 * appears) while preserving the real native scroll container underneath —
 * keyboard scrolling, `scrollIntoView`, anchor links, and IntersectionObserver
 * all continue to work as expected.
 *
 * Prefer this over setting `overflow: auto` on a plain `div` whenever a
 * scrollbar might be visible to the user.
 *
 * Imperative access (e.g. to reach the native viewport for
 * `IntersectionObserver` or to programmatically scroll) is available via
 * `ref.current?.osInstance()?.elements().viewport`.
 */
export const ScrollView = memoForwardRef(({ axis = "y", autoHide = "leave", theme = "light", autoHideDelay = 800, trackOffset, gutterPadding, endInset, className = "", style, children, ...rest }, ref) => {
    const themeClass = theme === "dark" ? "os-theme-light" : "os-theme-dark";
    const varStyle = {};
    if (trackOffset !== undefined) {
        varStyle["--putty-scroll-view-track-offset"] = `${trackOffset}px`;
    }
    if (gutterPadding !== undefined) {
        varStyle["--putty-scroll-view-gutter-padding"] = `${gutterPadding}px`;
    }
    if (endInset !== undefined) {
        varStyle["--putty-scroll-view-end-inset"] = `${endInset}px`;
    }
    const mergedStyle = { ...varStyle, ...style };
    return (_jsx(OverlayScrollbarsComponent, { ...rest, ref: ref, defer: true, className: `putty-scroll-view ${className}`.trim(), style: mergedStyle, options: {
            scrollbars: {
                theme: themeClass,
                autoHide,
                autoHideDelay,
            },
            overflow: {
                x: axis === "y" ? "hidden" : "scroll",
                y: axis === "x" ? "hidden" : "scroll",
            },
        }, children: children }));
});
